/**
 * @fileoverview Practitioner names sourced from firm field presets
 * @module lib/practitioner-presets
 *
 * Purpose:
 * - Derive the list of registered practitioners a firm has configured under
 *   Settings → Fields → Practitioner Details.
 *
 * Used by:
 * - Generate precedents wizard and the day-one Edit Fields panel, to populate
 *   the "Second Practitioner Name" picker. A joint appointee must be one of
 *   the firm's registered practitioners, so the job team list is not a valid
 *   source for that field.
 */

/** Canonical category id for the "Practitioner Details" field group. */
const PRACTITIONER_CATEGORY_ID = "practitioner";

/** Canonical field key holding a practitioner's full name within a preset. */
const PRACTITIONER_NAME_KEY = "practitioner_name";

type FieldPresetLike = {
  categoryId: string;
  values: Record<string, string>;
};

/**
 * Extract the distinct practitioner names from an org's field presets.
 *
 * @param presets - All `fieldPresets` rows for the org (any category).
 * @returns Alphabetically sorted, de-duplicated practitioner names.
 *
 * @example
 * getPresetPractitionerNames(allPresets); // ["Andrew Blundell", "Jane Smith"]
 */
export function getPresetPractitionerNames(
  presets: ReadonlyArray<FieldPresetLike> | undefined | null,
): string[] {
  if (!presets) return [];

  const names = new Set<string>();
  for (const preset of presets) {
    if (preset.categoryId !== PRACTITIONER_CATEGORY_ID) continue;
    const name = preset.values?.[PRACTITIONER_NAME_KEY]?.trim();
    if (name) names.add(name);
  }

  return Array.from(names).sort((a, b) => a.localeCompare(b));
}
